import { useRef, useEffect, useCallback, useMemo, useState, useLayoutEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Settings, Users, ArrowLeft, MessageSquare, Loader2 } from 'lucide-react'
import { useChatRoom } from '@/hooks/useChat'
import { ChatMessage, DateSeparator } from './ChatMessage'
import { ChatInput } from './ChatInput'
import { ChatSettingsDialog } from './ChatSettingsDialog'
import { gravatarUrl } from '@/lib/utils'

/**
 * ChatRoom component - displays the active chat conversation.
 *
 * Features:
 * - Message list with infinite scroll (load more on scroll to top)
 * - Auto-scroll to bottom for new messages
 * - Message grouping by date
 * - Real-time message updates via WebSocket
 * - Scroll position preservation when loading older messages
 */

interface ChatRoomProps {
  onBack?: () => void
}

export function ChatRoom({ onBack }: ChatRoomProps) {
  const { roomId: chatId } = useParams()
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)

  // Track scroll position for preserving it when loading older messages
  const prevScrollHeightRef = useRef<number>(0)
  const isLoadingOlderRef = useRef<boolean>(false)

  const {
    chat,
    messages,
    isLoading,
    error,
    sendMessage,
    isSending,
    currentUsername,
    loadOlderMessages,
    hasOlderMessages,
    isLoadingOlder,
  } = useChatRoom(chatId || null)

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = useCallback((smooth = true) => {
    messagesEndRef.current?.scrollIntoView({
      behavior: smooth ? 'smooth' : 'auto',
    })
  }, [])

  // Track if user is near bottom (for auto-scroll on new messages)
  const isNearBottomRef = useRef(true)
  const initialScrollDone = useRef(false)
  const prevMessagesLengthRef = useRef(0)

  // Scroll to bottom on initial load
  useEffect(() => {
    if (messages.length > 0 && !initialScrollDone.current) {
      scrollToBottom(false) // instant scroll on initial load
      initialScrollDone.current = true
      isNearBottomRef.current = true
    }
  }, [messages.length, scrollToBottom])

  // Auto-scroll to bottom when NEW messages arrive (not older ones)
  useEffect(() => {
    // Only scroll if we have more messages than before (new message, not older loaded)
    // and user is near the bottom
    if (messages.length > prevMessagesLengthRef.current && isNearBottomRef.current) {
      scrollToBottom(true) // smooth scroll for new messages
    }
    prevMessagesLengthRef.current = messages.length
  }, [messages.length, scrollToBottom])

  // Reset flags when chat changes
  useEffect(() => {
    initialScrollDone.current = false
    isNearBottomRef.current = true
    prevMessagesLengthRef.current = 0
  }, [chatId])

  // Track if user is near bottom (for auto-scroll decision)
  const updateNearBottom = useCallback(() => {
    const container = messagesContainerRef.current
    if (!container) return

    const threshold = 150 // pixels from bottom
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight
    isNearBottomRef.current = distanceFromBottom < threshold
  }, [])

  // Preserve scroll position when older messages are loaded
  // This runs synchronously after DOM updates to prevent visual jumping
  useLayoutEffect(() => {
    const container = messagesContainerRef.current
    if (!container) return

    // If we were loading older messages and they just loaded
    if (isLoadingOlderRef.current && !isLoadingOlder) {
      // Calculate how much content was added at the top
      const newScrollHeight = container.scrollHeight
      const addedHeight = newScrollHeight - prevScrollHeightRef.current

      // Adjust scroll position to compensate for new content
      if (addedHeight > 0) {
        container.scrollTop = addedHeight
      }
    }

    // Update ref to track loading state
    isLoadingOlderRef.current = isLoadingOlder
  }, [isLoadingOlder])

  // Handle scroll to load older messages and track position
  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current
    if (!container) return

    // Track if user is near bottom for auto-scroll on new messages
    updateNearBottom()

    // Load more when scrolled near the top (within 100px)
    if (!isLoadingOlder && hasOlderMessages && container.scrollTop < 100) {
      // Save current scroll height before loading
      prevScrollHeightRef.current = container.scrollHeight
      loadOlderMessages()
    }
  }, [isLoadingOlder, hasOlderMessages, loadOlderMessages, updateNearBottom])

  // Attach scroll listener
  useEffect(() => {
    const container = messagesContainerRef.current
    if (!container) return

    container.addEventListener('scroll', handleScroll)
    return () => container.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  // Group messages by date for separators
  const groupedMessages = useMemo(() => {
    const groups: Array<{ date: string; messages: typeof messages }> = []
    let currentDate = ''

    messages.forEach((message) => {
      const messageDate = new Date(message.dateSent).toDateString()
      if (messageDate !== currentDate) {
        currentDate = messageDate
        groups.push({ date: message.dateSent, messages: [] })
      }
      groups[groups.length - 1].messages.push(message)
    })

    return groups
  }, [messages])

  // Handle message send
  const handleSend = useCallback(
    async (content: string) => {
      await sendMessage(content)
    },
    [sendMessage]
  )

  // Handle quote action
  const handleQuote = useCallback((_messageId: string) => {
    // TODO: Implement quote functionality - will populate input with quoted text
    // This requires lifting state or using a ref to the ChatInput component
  }, [])

  // Handle delete action
  const handleDelete = useCallback((_messageId: string) => {
    // TODO: Implement delete functionality - will call API to delete message
    // api.delete(`/api/v2/messages/${messageId}`)
  }, [])

  // No chat selected state
  if (!chatId) {
    return (
      <div className="flex h-full flex-col items-center justify-center bg-gray-50 p-8 text-center">
        <MessageSquare className="mb-4 h-16 w-16 text-text-muted" />
        <h2 className="mb-2 text-xl font-semibold text-text-dark">Select a chat</h2>
        <p className="text-text-light">Choose a conversation from the list to start chatting</p>
      </div>
    )
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <div className="mb-4 h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <p className="text-text-light">Loading messages...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-8 text-center">
        <p className="mb-4 text-danger">Failed to load messages</p>
        <button
          onClick={() => window.location.reload()}
          className="rounded-md bg-primary px-4 py-2 text-white hover:bg-primary-dark"
        >
          Retry
        </button>
      </div>
    )
  }

  // Build display name
  const displayName =
    chat?.chatName || chat?.participants.map((p) => p.username).join(', ') || 'Chat'

  return (
    <div className="flex h-full flex-col">
      {/* Chat header */}
      <div className="flex items-center gap-3 border-b border-gray-200 bg-white px-4 py-3">
        {/* Back button (mobile) - touch-friendly size */}
        {onBack && (
          <button
            onClick={onBack}
            className="touch-target flex items-center justify-center rounded-md p-2 hover:bg-gray-100 lg:hidden"
            aria-label="Back to chat list"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
        )}

        {/* Chat avatar */}
        {chat && (
          <img
            src={gravatarUrl(chat.owner.gravatarHash, 40)}
            alt={chat.owner.username}
            className="h-10 w-10 rounded-full"
          />
        )}

        {/* Chat info */}
        <div className="flex-1 overflow-hidden">
          <h2 className="truncate font-semibold text-text-dark">{displayName}</h2>
          {chat && (
            <p className="truncate text-sm text-text-light">
              {chat.groupChat ? (
                <>
                  <Users className="mr-1 inline h-3 w-3" />
                  {chat.participants.length} members
                </>
              ) : (
                <>
                  {chat.participants.find((p) => p.username !== currentUsername)?.username ||
                    'Private chat'}
                </>
              )}
            </p>
          )}
        </div>

        {/* Chat settings button - touch-friendly size */}
        <button
          onClick={() => setIsSettingsOpen(true)}
          className="touch-target flex items-center justify-center rounded-md p-2 text-text-light hover:bg-gray-100 hover:text-primary"
          title="Chat settings"
        >
          <Settings className="h-5 w-5" />
        </button>
      </div>

      {/* Chat Settings Dialog */}
      {chat && (
        <ChatSettingsDialog
          chat={chat}
          isOpen={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
          currentUsername={currentUsername || ''}
        />
      )}

      {/* Messages */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto bg-gray-50">
        {messages.length === 0 ? (
          <div className="flex h-full items-center justify-center p-8 text-center">
            <div>
              <MessageSquare className="mx-auto mb-4 h-12 w-12 text-text-muted" />
              <p className="text-text-light">No messages yet</p>
              <p className="text-sm text-text-muted">Be the first to send a message!</p>
            </div>
          </div>
        ) : (
          <div className="py-4">
            {/* Loading indicator for older messages */}
            {isLoadingOlder && (
              <div className="flex justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              </div>
            )}

            {/* "Load more" hint when there are older messages */}
            {hasOlderMessages && !isLoadingOlder && (
              <div className="py-2 text-center text-sm text-text-muted">
                Scroll up to load older messages
              </div>
            )}

            {groupedMessages.map((group) => (
              <div key={group.date}>
                {/* Date separator */}
                <DateSeparator date={group.date} />

                {/* Messages in this group */}
                {group.messages.map((message) => (
                  <ChatMessage
                    key={message.id}
                    message={message}
                    isOwnMessage={message.sender.username === currentUsername}
                    onQuote={handleQuote}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            ))}

            {/* Scroll anchor */}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Message input */}
      <ChatInput
        onSend={handleSend}
        disabled={isSending || chat?.readonly}
        placeholder={chat?.readonly ? 'This chat is read-only' : 'Type a message...'}
      />
    </div>
  )
}
